import { Place as SiteIcon } from "@mui/icons-material";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import React from "react";

import { EntityRef } from "../../types/productPlane";
import { ChainFacility, ChainMaker } from "../../types/valueChain";
import EntityLink from "../Detail/EntityLink";

// What a cell reads where the read holds nothing
const NOTHING = "—";

// One production site of a kind, with the makers whose products of the kind
// are produced there
export interface KindFacility {
  site: ChainFacility;
  makers: EntityRef[];
}

/**
 * The production sites of a kind's makers: every facility the makers'
 * products of the kind are produced at, each once, with the makers produced
 * there, in the order the makers and their sites come in the read.
 */
export const kindFacilities = (makers: readonly ChainMaker[]): KindFacility[] => {
  const byId = new Map<string, KindFacility>();
  for (const maker of makers) {
    for (const site of maker.facilities) {
      const found = byId.get(site.facility.id);
      if (!found) {
        byId.set(site.facility.id, { site, makers: [maker.organization] });
      } else if (
        !found.makers.some((known) => known.id === maker.organization.id)
      ) {
        found.makers.push(maker.organization);
      }
    }
  }
  return Array.from(byId.values());
};

/**
 * The facilities of a kind as a table: the site, which opens the facility
 * view, its country and the makers produced there.
 */
const KindFacilities: React.FC<{ facilities: KindFacility[] }> = ({
  facilities,
}) => (
  <Box sx={{ overflowX: "auto" }}>
    <Table size="small" aria-label="Facilities">
      <TableHead>
        <TableRow>
          <TableCell>Facility</TableCell>
          <TableCell>Country</TableCell>
          <TableCell>Maker</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {facilities.map(({ site, makers }) => (
          <TableRow key={site.facility.id}>
            <TableCell>
              <Box
                component="span"
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
              >
                <SiteIcon
                  aria-hidden
                  sx={{ fontSize: 14, color: "success.main" }}
                />
                <EntityLink
                  id={site.facility.id}
                  type={site.facility.type}
                  name={site.facility.name}
                />
              </Box>
            </TableCell>
            <TableCell>{site.country || NOTHING}</TableCell>
            <TableCell>
              {makers.map((maker, index) => (
                <React.Fragment key={maker.id}>
                  {index > 0 && ", "}
                  <EntityLink id={maker.id} type={maker.type} name={maker.name} />
                </React.Fragment>
              ))}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Box>
);

export default KindFacilities;
